/**
 * Role definitions shared by the server guard and the web UI.
 * Project roles are ordered: a higher rank implies every capability of the ranks below it.
 */
export const PROJECT_ROLES = ['viewer', 'editor', 'admin', 'owner'] as const;
export type ProjectRole = (typeof PROJECT_ROLES)[number];

export const GLOBAL_ROLES = ['user', 'superadmin'] as const;
export type GlobalRole = (typeof GLOBAL_ROLES)[number];

const RANK: Record<ProjectRole, number> = { viewer: 0, editor: 1, admin: 2, owner: 3 };

export function roleRank(role: ProjectRole): number {
  return RANK[role] ?? -1;
}

/** True when `role` is at least as privileged as `required`. */
export function roleAtLeast(role: ProjectRole | null | undefined, required: ProjectRole): boolean {
  if (!role) return false;
  return roleRank(role) >= roleRank(required);
}

export const ROLE_LABELS: Record<ProjectRole, string> = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
  owner: 'Owner',
};

export const ROLE_DESCRIPTIONS: Record<ProjectRole, string> = {
  viewer:
    'Consumer access. Sees dashboards and the API catalogue with its docs, and can call published endpoints. Cannot see members, datasources, run logs or settings.',
  editor: 'Can build and publish APIs, datasources and dashboards.',
  admin: 'Everything an editor can do, plus members, tokens and project settings.',
  owner: 'Full control including transferring or deleting the project.',
};
